import { useState, useEffect } from 'react';
import { X, Mail, Paperclip, Upload, FileText, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Templates de respaldo en caso de que el backend falle
const FALLBACK_TEMPLATES = [
  {
    id: 'solicitud_documento',
    nombre: 'Solicitud de Documento',
    tipo: 'solicitud_documento',
    asunto: 'Kompa - Solicitud de Documento Adicional',
    cuerpo: `<p>Estimado(a) cliente,</p>
<p>Para continuar con el proceso de su retiro, necesitamos que nos envíe el siguiente documento:</p>
<p><strong>[ESPECIFICAR DOCUMENTO]</strong></p>
<p>Por favor, responda a este correo con el archivo adjunto o utilice el enlace que le proporcionaremos.</p>
<p>Gracias por su colaboración.</p>
<p>Saludos,<br>Equipo Kompa</p>`
  },
  {
    id: 'envio_calculo',
    nombre: 'Envío de Cálculo de Reembolso',
    tipo: 'envio_calculo',
    asunto: 'Kompa - Cálculo de Reembolso Listo',
    cuerpo: `<p>Estimado(a) cliente,</p>
<p>Adjunto encontrará el cálculo detallado de su reembolso.</p>
<p>Por favor, revíselo cuidadosamente. Si tiene alguna pregunta, no dude en contactarnos.</p>
<p><strong>Próximos pasos:</strong></p>
<ol>
  <li>Revisar el cálculo adjunto</li>
  <li>Firmar la declaración jurada</li>
  <li>Esperar la autorización de pago</li>
</ol>
<p>Saludos,<br>Equipo Kompa</p>`
  },
  {
    id: 'notificacion_general',
    nombre: 'Notificación General',
    tipo: 'notificacion_general',
    asunto: 'Kompa - Actualización de su Expediente',
    cuerpo: `<p>Estimado(a) cliente,</p>
<p>Le escribimos para informarle sobre una actualización en su expediente de retiro.</p>
<p>[ESCRIBA AQUÍ EL MENSAJE]</p>
<p>Si tiene alguna pregunta, estamos a su disposición.</p>
<p>Saludos,<br>Equipo Kompa</p>`
  },
  {
    id: 'solicitud_firma',
    nombre: 'Solicitud de Firma de Declaración',
    tipo: 'solicitud_firma',
    asunto: 'Kompa - Firma de Declaración Jurada Pendiente',
    cuerpo: `<p>Estimado(a) cliente,</p>
<p>Para continuar con el proceso de pago, necesitamos que firme la declaración jurada adjunta.</p>
<p><strong>Instrucciones:</strong></p>
<ol>
  <li>Descargue el documento adjunto</li>
  <li>Fírmelo de manera manuscrita o digital</li>
  <li>Envíelo de vuelta a través del enlace que le proporcionaremos</li>
</ol>
<p>Gracias por su colaboración.</p>
<p>Saludos,<br>Equipo Kompa</p>`
  },
  {
    id: 'personalizado',
    nombre: '📝 Mensaje Personalizado',
    tipo: 'personalizado',
    asunto: '',
    cuerpo: '<p></p>'
  }
];

export default function CentroComunicacionesModal({ isOpen, onClose, expedienteId, destinatario, onSuccess }) {
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSeleccionado, setTemplateSeleccionado] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [cuerpoOriginalHTML, setCuerpoOriginalHTML] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [incluirEnlace, setIncluirEnlace] = useState(false);

  // Cargar templates desde el backend
  useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/email-templates`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Agregar opción de personalizado al final
            const templatesConPersonalizado = [
              ...data,
              {
                id: 'personalizado',
                nombre: '📝 Mensaje Personalizado',
                tipo: 'personalizado',
                asunto: '',
                cuerpo: '<p></p>'
              }
            ];
            setTemplates(templatesConPersonalizado);
          } else {
            // Si no hay templates, usar los de respaldo
            setTemplates(FALLBACK_TEMPLATES);
          }
        } else {
          // Si falla, usar templates de respaldo
          console.warn('No se pudieron cargar templates del servidor, usando templates de respaldo');
          setTemplates(FALLBACK_TEMPLATES);
        }
      } catch (error) {
        console.error('Error al cargar templates:', error);
        setTemplates(FALLBACK_TEMPLATES);
      } finally {
        setLoadingTemplates(false);
      }
    };

    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // Reset cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setTemplateSeleccionado('');
      setAsunto('');
      setCuerpo('');
      setCuerpoOriginalHTML(null);
      setArchivos([]);
      setIncluirEnlace(false);
    }
  }, [isOpen]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setTemplateSeleccionado(templateId);
    
    if (templateId) {
      const template = templates.find(t => t.id === templateId || t.tipo === templateId);
      if (template) {
        setAsunto(template.asunto);
        // Guardar HTML original para preservar botones styled al enviar
        setCuerpoOriginalHTML(template.cuerpo);
        // Mostrar versión texto plano en el editor
        const textoPlano = template.cuerpo
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p><p>/gi, '\n\n')
          .replace(/<[^>]*>/g, '')
          .trim();
        setCuerpo(textoPlano);
      }
    } else {
      setCuerpoOriginalHTML(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    
    // Validar tamaño (max 15MB por archivo)
    const maxSize = 15 * 1024 * 1024;
    const filesValidos = newFiles.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} es demasiado grande (máx 15MB)`);
        return false;
      }
      return true;
    });
    
    setArchivos(prev => [...prev, ...filesValidos]);
  };

  const eliminarArchivo = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const handleEnviar = async () => {
    // Validaciones
    if (!asunto.trim()) {
      toast.error('Por favor ingrese un asunto');
      return;
    }
    
    if (!cuerpo.trim()) {
      toast.error('Por favor escriba un mensaje');
      return;
    }

    setEnviando(true);
    
    try {
      // Si hay template original con HTML (botones styled), usarlo directamente
      // Solo usar conversión texto→HTML si el usuario eligió personalizado o editó el mensaje
      let cuerpoHTML;
      if (cuerpoOriginalHTML && templateSeleccionado !== 'personalizado') {
        // Usar HTML original de la plantilla (preserva botones styled)
        cuerpoHTML = cuerpoOriginalHTML;
        // Agregar enlace de documentos si se marcó el checkbox
        if (incluirEnlace) {
          const enlaceCargarDocumentos = `${window.location.origin}/cargar-documentos`;
          cuerpoHTML += `<hr style="border-color: #ddd; margin: 20px 0;"><p>Para cargar documentos adicionales, utilice el siguiente enlace:</p><a href="${enlaceCargarDocumentos}" style="background-color: #DAA520; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; margin: 10px 0;">Cargar Documentos</a>`;
        }
      } else {
        // Mensaje personalizado: convertir texto plano a HTML
        let cuerpoFinal = cuerpo;
        if (incluirEnlace) {
          const enlaceCargarDocumentos = `${window.location.origin}/cargar-documentos`;
          cuerpoFinal += `\n\n---\nPara cargar documentos adicionales, utilice el siguiente enlace:\n${enlaceCargarDocumentos}\n\nNecesitará su cédula y email para acceder.`;
        }
        cuerpoHTML = `<p>${cuerpoFinal.split('\n').map(line => line.trim()).filter(line => line).join('</p><p>')}</p>`;
      }

      const formData = new FormData();
      formData.append('asunto', asunto);
      formData.append('cuerpo_html', cuerpoHTML);
      
      // Agregar archivos adjuntos
      archivos.forEach((archivo, index) => {
        formData.append(`adjuntos`, archivo);
      });

      const response = await fetch(`${BACKEND_URL}/api/expedientes/${expedienteId}/enviar-correo-personalizado`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ Correo enviado exitosamente a ${destinatario}`);
        onClose();
        if (onSuccess) onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Error al enviar correo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al enviar correo');
    } finally {
      setEnviando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Mail className="w-6 h-6 text-yellow-500" />
              Centro de Comunicaciones
            </h2>
            <p className="text-sm text-gray-400 mt-1">Destinatario: {destinatario}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Selector de Template */}
          <div>
            <Label className="text-gray-300 mb-2 block">Plantilla de Correo</Label>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Cargando plantillas...</span>
              </div>
            ) : (
              <select
                value={templateSeleccionado}
                onChange={handleTemplateChange}
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-yellow-500"
              >
                <option value="">Seleccionar plantilla...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id || template.tipo}>
                    {template.nombre}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Asunto */}
          <div>
            <Label className="text-gray-300 mb-2 block">Asunto *</Label>
            <input
              type="text"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder="Ingrese el asunto del correo..."
              className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-yellow-500"
            />
          </div>

          {/* Cuerpo del Mensaje */}
          <div>
            <Label className="text-gray-300 mb-2 block">Mensaje *</Label>
            <textarea
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              placeholder="Escriba aquí el mensaje para el cliente..."
              rows={12}
              className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg p-3 focus:outline-none focus:border-yellow-500 font-sans text-sm resize-none"
              style={{ whiteSpace: 'pre-wrap' }}
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Use Enter para saltos de línea. El texto se enviará formateado.
            </p>
          </div>

          {/* Checkbox para incluir enlace */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="incluir-enlace"
                checked={incluirEnlace}
                onChange={(e) => setIncluirEnlace(e.target.checked)}
                className="mt-1 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500 focus:ring-2"
              />
              <div className="flex-1">
                <label htmlFor="incluir-enlace" className="text-blue-300 font-medium cursor-pointer flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Incluir enlace para cargar documentos
                </label>
                <p className="text-blue-400 text-xs mt-1">
                  Se agregará automáticamente un enlace a <strong>/cargar-documentos</strong> al final del correo para que el cliente pueda subir archivos adicionales.
                </p>
              </div>
            </div>
          </div>

          {/* Zona de Adjuntos */}
          <div>
            <Label className="text-gray-300 mb-2 block flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              Archivos Adjuntos (Opcional)
            </Label>
            
            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-yellow-500 bg-yellow-500/10' 
                  : 'border-gray-600 bg-gray-800/50'
              }`}
            >
              <Upload className={`w-12 h-12 mx-auto mb-3 ${dragActive ? 'text-yellow-500' : 'text-gray-500'}`} />
              <p className="text-gray-300 mb-2">
                Arrastra archivos aquí o haz click para seleccionar
              </p>
              <p className="text-xs text-gray-500 mb-4">
                PDF, imágenes, documentos (máx 15MB por archivo)
              </p>
              <input
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              />
              <label htmlFor="file-upload">
                <Button
                  type="button"
                  variant="outline"
                  className="cursor-pointer border-gray-600 text-gray-300 hover:bg-gray-700"
                  asChild
                >
                  <span>
                    <FileText className="w-4 h-4 mr-2" />
                    Seleccionar Archivos
                  </span>
                </Button>
              </label>
            </div>

            {/* Lista de Archivos Seleccionados */}
            {archivos.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-400 font-semibold">
                  Archivos seleccionados ({archivos.length}):
                </p>
                {archivos.map((archivo, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-yellow-500" />
                      <div>
                        <p className="text-sm text-white font-medium">{archivo.name}</p>
                        <p className="text-xs text-gray-500">
                          {(archivo.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => eliminarArchivo(index)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-700 p-6 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={enviando}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={enviando}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
          >
            {enviando ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Enviar Correo
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
